import React, { useRef, useState } from 'react';
import { Camera, ImagePlus, Loader2, Trash2, X } from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { easerPost, easerDelete, easerPut, errorMessage, isUnauthorized, compressImage, BACKEND_URL, getToken } from '../../../lib/easerApi';

export const MAX_PHOTOS = 12;
export const photoUrl = (id) => `${BACKEND_URL}/api/easer/devis-green/photos/${id}?token=${encodeURIComponent(getToken() || '')}`;

/**
 * Photos du terrain : prises avec l'appareil ou choisies dans la galerie, compressées (1600 px) puis envoyées
 * au serveur. Elles sont jointes au devis Dolibarr à la génération et restent visibles dans l'historique.
 * `photos` : [{id, caption, ...}] ; `draftId` lie les photos au brouillon courant (facultatif).
 */
export function DevisPhotos({ photos, onChange, draftId, onUnauthorized, readOnly = false }) {
  const [busy, setBusy] = useState(0);
  const [preview, setPreview] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const addFiles = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
    if (!list.length) return toast.info(`${MAX_PHOTOS} photos maximum`);
    setBusy(list.length);
    const added = [];
    for (const file of list) {
      try {
        const data = await compressImage(file, 1600, 0.82);
        const r = await easerPost(`/api/easer/devis-green/photos${draftId ? `?draft_id=${encodeURIComponent(draftId)}` : ''}`, { data });
        added.push(r.data);
      } catch (err) {
        if (isUnauthorized(err)) return onUnauthorized();
        toast.error(errorMessage(err, `Photo « ${file.name} » non envoyée`));
      } finally {
        setBusy((b) => b - 1);
      }
    }
    if (added.length) onChange([...photos, ...added]);
  };

  const remove = async (p) => {
    try {
      await easerDelete(`/api/easer/devis-green/photos/${p.id}`);
      onChange(photos.filter((x) => x.id !== p.id));
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Suppression impossible'));
    }
  };

  const caption = (p, value) => onChange(photos.map((x) => (x.id === p.id ? { ...x, caption: value } : x)));
  const saveCaption = (p) => easerPut(`/api/easer/devis-green/photos/${p.id}`, { caption: p.caption || '' }).catch(() => { /* légende locale conservée */ });

  return (
    <Card className="p-4 space-y-3" data-testid="devis-photos">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold flex items-center gap-2"><Camera className="h-4 w-4 text-[hsl(var(--primary))]" /> Photos du terrain <span className="text-xs font-normal text-muted-foreground">({photos.length}/{MAX_PHOTOS})</span></p>
          <p className="text-xs text-muted-foreground">État constaté avant travaux — jointes au devis Dolibarr.</p>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" className="h-10" onClick={() => cameraRef.current?.click()} disabled={busy > 0 || photos.length >= MAX_PHOTOS} data-testid="devis-photo-camera"><Camera className="h-4 w-4 mr-1" /> Photo</Button>
            <Button type="button" variant="outline" size="sm" className="h-10" onClick={() => galleryRef.current?.click()} disabled={busy > 0 || photos.length >= MAX_PHOTOS} data-testid="devis-photo-gallery"><ImagePlus className="h-4 w-4 mr-1" /> Galerie</Button>
          </div>
        )}
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="devis-photo-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="devis-photo-gallery-input" />
      {busy > 0 && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi de {busy} photo{busy > 1 ? 's' : ''}…</p>}
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2" data-testid="devis-photo-grid">
          {photos.map((p) => (
            <figure key={p.id} className="relative rounded-lg overflow-hidden border border-border bg-secondary/40" data-testid={`devis-photo-${p.id}`}>
              <button type="button" className="block w-full aspect-square" onClick={() => setPreview(p)} aria-label="Agrandir la photo">
                <img src={photoUrl(p.id)} alt={p.caption || 'Photo du terrain'} className="h-full w-full object-cover" loading="lazy" />
              </button>
              {!readOnly && (
                <>
                  <button type="button" onClick={() => remove(p)} className="absolute top-1 right-1 h-7 w-7 inline-flex items-center justify-center rounded-full bg-background/90 text-destructive shadow" aria-label="Supprimer la photo" data-testid={`devis-photo-${p.id}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
                  <Input value={p.caption || ''} onChange={(e) => caption(p, e.target.value)} onBlur={() => saveCaption(p)} placeholder="Légende" className="h-8 text-xs rounded-none border-0 border-t border-border bg-card" maxLength={120} data-testid={`devis-photo-${p.id}-caption`} />
                </>
              )}
              {readOnly && p.caption && <figcaption className="text-[11px] px-1.5 py-1 truncate">{p.caption}</figcaption>}
            </figure>
          ))}
        </div>
      ) : (
        !readOnly && <p className="text-sm text-muted-foreground" data-testid="devis-photo-empty">Aucune photo pour l'instant.</p>
      )}
      {preview && (
        <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4" onClick={() => setPreview(null)} role="dialog" aria-label="Aperçu photo" data-testid="devis-photo-preview">
          <button type="button" className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/15 text-white" onClick={() => setPreview(null)} aria-label="Fermer"><X className="h-5 w-5" /></button>
          <img src={photoUrl(preview.id)} alt={preview.caption || 'Photo du terrain'} className="max-h-full max-w-full object-contain rounded-lg" />
          {preview.caption && <p className="absolute bottom-6 left-0 right-0 text-center text-white text-sm">{preview.caption}</p>}
        </div>
      )}
    </Card>
  );
}
